/**
 * Readable time spans of calendar entries — „Mo., 10.05.2026, 10:00 – 11:00“.
 * Built on the kit's cached `Intl` formatters, so the host's locale decides the
 * order, the separators and the 12/24-hour clock.
 */
import { formatTime } from '../Format/date';
import { dateTimeFormat } from '../Format/intlCache';
import { addLocalDays, startOfLocalDay } from './eventDays';

const DAY_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
};

export interface NxFormatEventRangeOptions {
  /** BCP-47 tag; the runtime default when left out. */
  locale?: string;
  allDay?: boolean;
  /** Appended to all-day spans in brackets. Defaults to „ganztägig“; `''` shows none. */
  allDayLabel?: string;
  /** Shown for an unreadable start. */
  fallback?: string;
}

function valid(date: Date | null | undefined): date is Date {
  return date instanceof Date && Number.isFinite(date.getTime());
}

function sameLocalDay(a: Date, b: Date): boolean {
  return startOfLocalDay(a).getTime() === startOfLocalDay(b).getTime();
}

/** „09:00 – 10:00“, or „09:00“ for a point in time. */
export function formatTimeRange(start: Date, end?: Date | null, locale?: string): string {
  if (!valid(start)) return '';
  const from = formatTime(start, { locale });
  if (!valid(end) || end.getTime() <= start.getTime()) return from;
  return `${from} – ${formatTime(end, { locale })}`;
}

/**
 * The whole span with its dates. All-day `end` is exclusive (the midnight
 * after the last day), so the last day shown is the one before it — stepped
 * by calendar day, never by 24 hours, which would land on the wrong day
 * across a daylight-saving change.
 */
export function formatEventRange(
  start: Date,
  end?: Date | null,
  options: NxFormatEventRangeOptions = {}
): string {
  const { locale, allDay = false, allDayLabel = 'ganztägig', fallback = '' } = options;
  if (!valid(start)) return fallback;
  const dayFormat = dateTimeFormat(locale, DAY_OPTIONS);
  const effectiveEnd = valid(end) && end.getTime() > start.getTime() ? end : start;

  if (allDay) {
    const suffix = allDayLabel ? ` (${allDayLabel})` : '';
    const lastDay = addLocalDays(effectiveEnd, -1);
    const endsLater = effectiveEnd.getTime() > start.getTime() && !sameLocalDay(start, lastDay);
    return endsLater
      ? `${dayFormat.format(start)} – ${dayFormat.format(lastDay)}${suffix}`
      : `${dayFormat.format(start)}${suffix}`;
  }

  if (sameLocalDay(start, effectiveEnd)) {
    return `${dayFormat.format(start)}, ${formatTimeRange(start, effectiveEnd, locale)}`;
  }
  return `${dayFormat.format(start)}, ${formatTime(start, { locale })} – ${dayFormat.format(
    effectiveEnd
  )}, ${formatTime(effectiveEnd, { locale })}`;
}
